import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Search, Eye, EyeOff, User, Briefcase, Megaphone, ChevronDown } from 'lucide-react';

const TABS = [
  { key: 'profiles', label: 'Profiles', icon: User },
  { key: 'opportunities', label: 'Opportunities', icon: Briefcase },
  { key: 'briefs', label: 'Briefs', icon: Megaphone },
];

export default function AdminModeration() {
  const [activeTab, setActiveTab] = useState('profiles');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [toggling, setToggling] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterActive === 'active') params.append('is_active', 'true');
      if (filterActive === 'inactive') params.append('is_active', 'false');

      const response = await api.get(`/admin/${activeTab}?${params.toString()}`);
      const data = response.data;

      if (activeTab === 'profiles') {
        setItems(data.profiles || []);
        setTotal(data.total || 0);
      } else if (activeTab === 'opportunities') {
        setItems(data.opportunities || []);
        setTotal(data.total || 0);
      } else {
        setItems(data.briefs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [activeTab]);

  const handleToggle = async (id) => {
    setToggling(id);
    try {
      let endpoint;
      if (activeTab === 'profiles') endpoint = `/admin/profiles/${id}/toggle`;
      else if (activeTab === 'opportunities') endpoint = `/admin/opportunities/${id}/toggle`;
      else endpoint = `/admin/briefs/${id}/toggle`;

      await api.put(endpoint);
      fetchItems();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to toggle status');
    } finally {
      setToggling(null);
    }
  };

  const getId = (item) => {
    if (activeTab === 'profiles') return item.user_id;
    if (activeTab === 'opportunities') return item.opportunity_id;
    return item.brief_id;
  };

  const getTitle = (item) => {
    if (activeTab === 'profiles') return item.full_name || item.user?.name || 'Unnamed';
    return item.title || 'Untitled';
  };

  const getSubtitle = (item) => {
    if (activeTab === 'profiles') return item.headline || item.user?.email || '';
    return item.company_name || item.client?.name || '';
  };

  const isActive = (item) => item.is_active !== false;

  return (
    <DashboardLayout title="Content Moderation">
      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-neutral-100 p-1 w-fit" data-testid="moderation-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => { setActiveTab(tab.key); setSearch(''); setFilterActive(''); }}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
          <input
            type="text"
            data-testid="moderation-search"
            placeholder={`Search ${activeTab}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchItems()}
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 bg-white focus:border-[#1a1a2e] outline-none text-sm"
          />
        </div>
        <select
          data-testid="moderation-filter"
          value={filterActive}
          onChange={(e) => { setFilterActive(e.target.value); }}
          className="px-3 py-2.5 border border-neutral-200 bg-white text-sm"
        >
          <option value="">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Deactivated Only</option>
        </select>
        <button onClick={fetchItems} className="px-4 py-2.5 bg-[#1a1a2e] text-white text-sm font-medium hover:bg-[#2a2a4e]">
          Search
        </button>
      </div>

      <p className="text-sm text-neutral-500 mb-4">{total} {activeTab} found</p>

      {/* Items List */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a1a2e]"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <p className="text-neutral-500">No {activeTab} found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const id = getId(item);
            const active = isActive(item);
            return (
              <div
                key={id}
                data-testid={`moderation-item-${id}`}
                className={`bg-white border p-4 flex items-center gap-4 ${active ? 'border-neutral-200' : 'border-red-200 bg-red-50/30'}`}
              >
                <div className={`w-2 h-2 rounded-full ${active ? 'bg-green-500' : 'bg-red-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm ${!active ? 'text-neutral-400 line-through' : ''}`}>
                    {getTitle(item)}
                  </p>
                  <p className="text-xs text-neutral-500 truncate">{getSubtitle(item)}</p>
                </div>
                {activeTab !== 'profiles' && (
                  <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-600">{item.status}</span>
                )}
                <button
                  data-testid={`toggle-${id}`}
                  onClick={() => handleToggle(id)}
                  disabled={toggling === id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                      : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                  }`}
                >
                  {toggling === id ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                  ) : active ? (
                    <><EyeOff size={14} /> Deactivate</>
                  ) : (
                    <><Eye size={14} /> Activate</>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
